import React from 'react';
import { CalendarIcon } from '@revfluence/fresh-icons/regular/esm';
import { StarsIcon } from '@revfluence/fresh-icons/solid/esm';
import { ArrowDownToLineIcon, ArrowUpRightFromSquareIcon } from '@revfluence/fresh-icons/regular/esm';
import { ImageIcon } from '@revfluence/fresh-icons/regular/esm';
import { Card } from '@/shadcn/components/ui/card';
import { Badge } from '@/shadcn/components/ui/badge';
import { cn } from '@/shadcn/lib/utils';
import { Skeleton } from '@/shadcn/components/ui/skeleton';

export interface ContentPreviewImage {
  /** Image URL */
  url: string;
  /** Alt text for accessibility */
  alt?: string;
  /** Unique identifier for the image */
  id?: string;
}

export interface ContentPreviewMultiCardProps {
  /** Additional CSS classes */
  className?: string;
  /** Whether the component is in loading state */
  isLoading?: boolean;
  /** Children to render inside the card */
  children?: React.ReactNode;
}

// === COMPOSABLE COMPONENTS ===

// Root Card Component
const ContentPreviewMultiCard = React.forwardRef<HTMLDivElement, ContentPreviewMultiCardProps>(
  ({ className, isLoading = false, children, ...props }, ref) => {
    if (isLoading) {
      return <ContentPreviewMultiCardSkeleton className={className} />;
    }

    return (
      <Card ref={ref} className={cn('relative w-full aspect-[9/16] group overflow-hidden', className)} {...props}>
        {children}
      </Card>
    );
  },
);

ContentPreviewMultiCard.displayName = 'ContentPreviewMultiCard';

// Media Container Component
interface ContentPreviewMultiCardMediaProps {
  children?: React.ReactNode;
  className?: string;
}

const ContentPreviewMultiCardMedia: React.FC<ContentPreviewMultiCardMediaProps> = ({ children, className }) => (
  <div className="absolute inset-0">
    <ContentPreviewMultiCardBackground imageUrl="" />
    <ContentPreviewMultiCardImageContainer>
      <ContentPreviewMultiCardImageContent>
        {children}
      </ContentPreviewMultiCardImageContent>
    </ContentPreviewMultiCardImageContainer>
    {!className?.includes('no-gradient') && <ContentPreviewMultiCardGradient />}
  </div>
);

ContentPreviewMultiCardMedia.displayName = 'ContentPreviewMultiCardMedia';

// Background Component
interface ContentPreviewMultiCardBackgroundProps {
  imageUrl: string;
  className?: string;
}

const ContentPreviewMultiCardBackground: React.FC<ContentPreviewMultiCardBackgroundProps> = ({
  imageUrl,
  className
}) => (
  <div
    className={cn('absolute inset-0 w-full h-full bg-cover bg-center blur-md brightness-90 scale-110', className)}
    style={{
      backgroundImage: imageUrl ? `url(${imageUrl})` : 'none',
    }}
  />
);

ContentPreviewMultiCardBackground.displayName = 'ContentPreviewMultiCardBackground';

// Image Container Component
interface ContentPreviewMultiCardImageContainerProps {
  children?: React.ReactNode;
  className?: string;
}

const ContentPreviewMultiCardImageContainer: React.FC<ContentPreviewMultiCardImageContainerProps> = ({
  children,
  className
}) => (
  <div
    className={cn('relative h-full min-h-28 min-w-28 overflow-hidden rounded-lg md:min-h-36 md:min-w-36', className)}
  >
    {children}
  </div>
);

ContentPreviewMultiCardImageContainer.displayName = 'ContentPreviewMultiCardImageContainer';

// Image Content Component
interface ContentPreviewMultiCardImageContentProps {
  children?: React.ReactNode;
  className?: string;
}

const ContentPreviewMultiCardImageContent: React.FC<ContentPreviewMultiCardImageContentProps> = ({
  children,
  className
}) => (
  <div className={cn('relative h-full w-full', className)}>
    {children}
  </div>
);

ContentPreviewMultiCardImageContent.displayName = 'ContentPreviewMultiCardImageContent';

// Image Component
interface ContentPreviewMultiCardImageProps {
  src: string;
  alt: string;
  className?: string;
  onError?: () => void;
}

const ContentPreviewMultiCardImage: React.FC<ContentPreviewMultiCardImageProps> = ({
  src,
  alt,
  className,
  onError
}) => (
  <img
    alt={alt}
    className={cn('w-full h-full object-contain', className)}
    src={src}
    onError={onError}
  />
);

ContentPreviewMultiCardImage.displayName = 'ContentPreviewMultiCardImage';

// Placeholder Component
interface ContentPreviewMultiCardPlaceholderProps {
  className?: string;
}

const ContentPreviewMultiCardPlaceholder: React.FC<ContentPreviewMultiCardPlaceholderProps> = ({
  className
}) => (
  <div className={cn('w-full h-full bg-slate-800 flex items-center justify-center', className)}>
    <ImageIcon className="h-16 w-16 text-slate-500" />
  </div>
);

ContentPreviewMultiCardPlaceholder.displayName = 'ContentPreviewMultiCardPlaceholder';

// Gradient Component
interface ContentPreviewMultiCardGradientProps {
  className?: string;
}

const ContentPreviewMultiCardGradient: React.FC<ContentPreviewMultiCardGradientProps> = ({
  className
}) => (
  <div
    className={cn(
      'absolute inset-0 bg-gradient-to-b from-[#16282D]/80 via-[#16282D]/20 to-[#16282D]/80 pointer-events-none z-[2]',
      className,
    )}
  />
);

ContentPreviewMultiCardGradient.displayName = 'ContentPreviewMultiCardGradient';

// Content Container Component
interface ContentPreviewMultiCardContentProps {
  children?: React.ReactNode;
  className?: string;
}

const ContentPreviewMultiCardContent: React.FC<ContentPreviewMultiCardContentProps> = ({
  children,
  className
}) => (
  <div className={cn('relative h-full flex flex-col z-10', className)}>
    {children}
  </div>
);

ContentPreviewMultiCardContent.displayName = 'ContentPreviewMultiCardContent';

// Footer Component
interface ContentPreviewMultiCardFooterProps {
  children?: React.ReactNode;
  className?: string;
}

const ContentPreviewMultiCardFooter: React.FC<ContentPreviewMultiCardFooterProps> = ({
  children,
  className
}) => (
  <div className={cn('p-4 space-y-3', className)}>
    {children}
  </div>
);

ContentPreviewMultiCardFooter.displayName = 'ContentPreviewMultiCardFooter';

// Image Grid Component
interface ContentPreviewImageGridProps {
  images: ContentPreviewImage[];
  isImageLoading?: boolean;
  onImageClick?: (image: ContentPreviewImage, index: number) => void;
  onPlaceholderClick?: (index: number) => void;
}

const ContentPreviewImageGrid: React.FC<ContentPreviewImageGridProps> = ({
  images,
  isImageLoading,
  onImageClick,
  onPlaceholderClick
}) => {
  if (!images || images.length === 0) {
    return <ContentPreviewMultiCardPlaceholder />;
  }

  const displayImages: (ContentPreviewImage | null)[] = [...images];
  while (displayImages.length < 9) {
    displayImages.push(null);
  }

  return (
    <div className="grid grid-cols-3 grid-rows-3 gap-0 w-full h-full">
      {displayImages.slice(0, 9).map((image, index) => (
        <ContentPreviewImageGridCell
          image={image}
          index={index}
          isImageLoading={isImageLoading}
          onImageClick={onImageClick}
          onPlaceholderClick={onPlaceholderClick}
          key={image?.id || `placeholder-${index}`}
        />
      ))}
    </div>
  );
};

ContentPreviewImageGrid.displayName = 'ContentPreviewImageGrid';

// Image Grid Cell Component
interface ContentPreviewImageGridCellProps {
  image: ContentPreviewImage | null;
  index: number;
  isImageLoading?: boolean;
  onImageClick?: (image: ContentPreviewImage, index: number) => void;
  onPlaceholderClick?: (index: number) => void;
}

const ContentPreviewImageGridCell: React.FC<ContentPreviewImageGridCellProps> = ({
  image,
  index,
  isImageLoading,
  onImageClick,
  onPlaceholderClick
}) => {
  const [imageError, setImageError] = React.useState(false);

  const handleImageError = () => {
    setImageError(true);
  };

  const handleImageClick = () => {
    if (image && onImageClick) {
      onImageClick(image, index);
    }
  };

  const handlePlaceholderClick = () => {
    if (onPlaceholderClick) {
      onPlaceholderClick(index);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, callback: () => void) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      callback();
    }
  };

  if (!image) {
    return (
      <ContentPreviewImageGridPlaceholder
        index={index}
        onClick={handlePlaceholderClick}
        onKeyDown={(e) => handleKeyDown(e, handlePlaceholderClick)}
      />
    );
  }

  if (isImageLoading) {
    return (
      <ContentPreviewImageGridLoadingCell
        index={index}
        onClick={handlePlaceholderClick}
        onKeyDown={(e) => handleKeyDown(e, handlePlaceholderClick)}
      />
    );
  }

  if (image.url && !imageError) {
    return (
      <ContentPreviewImageGridImageCell
        image={image}
        index={index}
        onError={handleImageError}
        onClick={handleImageClick}
        onKeyDown={(e) => handleKeyDown(e, handleImageClick)}
      />
    );
  }

  return (
    <ContentPreviewImageGridPlaceholder
      index={index}
      onClick={handlePlaceholderClick}
      onKeyDown={(e) => handleKeyDown(e, handlePlaceholderClick)}
    />
  );
};

ContentPreviewImageGridCell.displayName = 'ContentPreviewImageGridCell';

// Placeholder Cell Component
interface ContentPreviewImageGridPlaceholderProps {
  index: number;
  onClick?: () => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  className?: string;
}

const ContentPreviewImageGridPlaceholder: React.FC<ContentPreviewImageGridPlaceholderProps> = ({
  index,
  onClick,
  onKeyDown,
  className
}) => {
  const isClickable = !!onClick;

  return (
    <div
      className={cn(
        'bg-grey-3 flex items-center justify-center',
        isClickable && 'cursor-pointer hover:bg-grey-4 transition-colors focus:outline-none focus:ring-2 focus:ring-white/20',
        className
      )}
      onClick={onClick}
      onKeyDown={onKeyDown}
      tabIndex={isClickable ? 0 : undefined}
      role={isClickable ? 'button' : undefined}
      aria-label={isClickable ? `Click placeholder at position ${index + 1}` : undefined}
    >
      <div className="w-3 h-3 bg-grey-5" />
    </div>
  );
};

ContentPreviewImageGridPlaceholder.displayName = 'ContentPreviewImageGridPlaceholder';

// Loading Cell Component
interface ContentPreviewImageGridLoadingCellProps {
  index: number;
  onClick?: () => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  className?: string;
}

const ContentPreviewImageGridLoadingCell: React.FC<ContentPreviewImageGridLoadingCellProps> = ({
  index,
  onClick,
  onKeyDown,
  className
}) => {
  const isClickable = !!onClick;

  return (
    <div
      className={cn(
        'bg-grey-3 flex items-center justify-center',
        isClickable && 'cursor-pointer hover:bg-grey-4 transition-colors focus:outline-none focus:ring-2 focus:ring-white/20',
        className
      )}
      onClick={onClick}
      onKeyDown={onKeyDown}
      tabIndex={isClickable ? 0 : undefined}
      role={isClickable ? 'button' : undefined}
      aria-label={isClickable ? `Click loading cell at position ${index + 1}` : undefined}
    >
      <div className="w-3 h-3 bg-grey-5 animate-pulse" />
    </div>
  );
};

ContentPreviewImageGridLoadingCell.displayName = 'ContentPreviewImageGridLoadingCell';

// Image Cell Component
interface ContentPreviewImageGridImageCellProps {
  image: ContentPreviewImage;
  index: number;
  onError?: () => void;
  onClick?: () => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  className?: string;
}

const ContentPreviewImageGridImageCell: React.FC<ContentPreviewImageGridImageCellProps> = ({
  image,
  index,
  onError,
  onClick,
  onKeyDown,
  className
}) => {
  const isClickable = !!onClick;

  return (
    <div
      className={cn(
        'relative overflow-hidden aspect-[9/16]',
        isClickable && 'cursor-pointer hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-white/20',
        className
      )}
      onClick={onClick}
      onKeyDown={onKeyDown}
      tabIndex={isClickable ? 0 : undefined}
      role={isClickable ? 'button' : undefined}
      aria-label={isClickable ? `Click image ${index + 1}: ${image.alt || 'Post image'}` : undefined}
    >
      <ContentPreviewMultiCardBackground imageUrl={image.url} />
      <div className="relative h-full w-full overflow-hidden">
        <ContentPreviewMultiCardImage
          src={image.url}
          alt={image.alt || `Post image ${index + 1}`}
          onError={onError}
          className="object-contain w-full h-full"
        />
      </div>
    </div>
  );
};

ContentPreviewImageGridImageCell.displayName = 'ContentPreviewImageGridImageCell';

// Actions Component
interface ContentPreviewMultiCardActionsProps {
  onDownload?: () => void;
  onOpenExternal?: () => void;
}

const ContentPreviewMultiCardActions: React.FC<ContentPreviewMultiCardActionsProps> = ({
  onDownload,
  onOpenExternal
}) => {
  const handleKeyDown = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      action();
    }
  };

  return (
    <div className="absolute top-3 right-3 flex gap-2">
      <div
        className="rounded-full p-1 cursor-pointer hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-white/20"
        onClick={onDownload}
        onKeyDown={(e) => onDownload && handleKeyDown(e, onDownload)}
        tabIndex={0}
        role="button"
        aria-label="Download content"
        title="Download content"
      >
        <ArrowDownToLineIcon className="h-4 w-4 text-white drop-shadow-lg" />
      </div>
      <div
        className="rounded-full p-1 cursor-pointer hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-white/20"
        onClick={onOpenExternal}
        onKeyDown={(e) => onOpenExternal && handleKeyDown(e, onOpenExternal)}
        tabIndex={0}
        role="button"
        aria-label="Open content in new window"
        title="Open in new window"
      >
        <ArrowUpRightFromSquareIcon className="h-4 w-4 text-white drop-shadow-lg" />
      </div>
    </div>
  );
};

ContentPreviewMultiCardActions.displayName = 'ContentPreviewMultiCardActions';

// Boost Badge Component
interface ContentPreviewBoostBadgeProps {
  isBoostEligible: boolean;
}

const ContentPreviewBoostBadge: React.FC<ContentPreviewBoostBadgeProps> = ({ isBoostEligible }) => {
  if (!isBoostEligible) {
    return null;
  }

  return (
    <div className="flex justify-start">
      <Badge variant="secondary" className="bg-green-4 text-white text-xs px-2 py-1 flex items-center gap-1">
        <StarsIcon className="h-3 w-3" />
        Boost Eligible
      </Badge>
    </div>
  );
};

ContentPreviewBoostBadge.displayName = 'ContentPreviewBoostBadge';

// Tags Component
interface ContentPreviewTagsProps {
  tags?: string[];
}

const ContentPreviewTags: React.FC<ContentPreviewTagsProps> = ({ tags }) => {
  if (!tags || tags.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center gap-1">
      {tags.map((tag, index) => (
        <Badge
          key={index}
          variant="secondary"
          className="bg-grey-4 text-white text-xs px-2 py-1 rounded-md"
        >
          #{tag}
        </Badge>
      ))}
    </div>
  );
};

ContentPreviewTags.displayName = 'ContentPreviewTags';

// Stats Component
interface ContentPreviewStatsProps {
  date?: string | Date | null;
}

const ContentPreviewStats: React.FC<ContentPreviewStatsProps> = ({
  date
}) => {
  const formatDate = (dateValue?: string | Date | null) => {
    if (!dateValue) return 'Unknown';

    const dateObj = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
    return dateObj.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="flex items-center gap-2">
      <CalendarIcon className="h-4 w-4" />
      <span>{formatDate(date)}</span>
    </div>
  );
};

ContentPreviewStats.displayName = 'ContentPreviewStats';

// Skeleton Component
interface ContentPreviewMultiCardSkeletonProps {
  className?: string;
}

const ContentPreviewMultiCardSkeleton: React.FC<ContentPreviewMultiCardSkeletonProps> = ({ className }) => (
  <Card className={cn('relative w-full aspect-[9/16] group overflow-hidden', className)}>
    <div className="absolute inset-0">
      <ContentPreviewMultiCardBackground imageUrl="" />
      <ContentPreviewMultiCardImageContainer>
        <ContentPreviewMultiCardImageContent>
          <div className="grid grid-cols-3 grid-rows-3 gap-0 w-full h-full">
            {Array.from({ length: 9 }).map((_, index) => (
              <div key={index} className="bg-grey-3 flex items-center justify-center">
                <Skeleton className="w-full h-full" />
              </div>
            ))}
          </div>
        </ContentPreviewMultiCardImageContent>
      </ContentPreviewMultiCardImageContainer>
      <ContentPreviewMultiCardGradient />
    </div>
    <div className="absolute top-3 right-3 flex gap-2">
      <Skeleton className="h-6 w-6 rounded-full" />
      <Skeleton className="h-6 w-6 rounded-full" />
    </div>
    <div className="flex-grow" />
    <ContentPreviewMultiCardFooter>
      <div className="flex justify-start">
        <Skeleton className="h-6 w-24 rounded-md" />
      </div>
      <div className="flex flex-wrap gap-1">
        <Skeleton className="h-5 w-16 rounded-md" />
        <Skeleton className="h-5 w-14 rounded-md" />
      </div>
      <div className="flex items-center gap-2">
        <Skeleton className="h-4 w-4 rounded" />
        <Skeleton className="h-4 w-20 rounded" />
      </div>
    </ContentPreviewMultiCardFooter>
  </Card>
);

ContentPreviewMultiCardSkeleton.displayName = 'ContentPreviewMultiCardSkeleton';

// === COMPOUND COMPONENT ===

export interface ContentPreviewMultiCardCompoundProps {
  /** Array of images to display in the grid (max 9, will be padded if fewer) */
  images?: ContentPreviewImage[];
  /** Post date */
  date?: string | Date | null;
  /** Array of tags to display */
  tags?: string[];
  /** Whether the content is eligible for boost */
  isBoostEligible?: boolean;
  /** Whether the component is in loading state */
  isLoading?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Callback for download action */
  onDownload?: () => void;
  /** Callback for opening external link */
  onOpenExternal?: () => void;
}

const ContentPreviewMultiCardCompound: React.FC<ContentPreviewMultiCardCompoundProps> = ({
  images = [],
  date,
  tags,
  isBoostEligible = false,
  isLoading = false,
  className,
  onDownload,
  onOpenExternal,
}) => {
  return (
    <ContentPreviewMultiCard isLoading={isLoading} className={className}>
      <ContentPreviewMultiCardMedia>
        <ContentPreviewImageGrid images={images} />
      </ContentPreviewMultiCardMedia>
      <ContentPreviewMultiCardContent>
        <ContentPreviewMultiCardActions onDownload={onDownload} onOpenExternal={onOpenExternal} />
        <div className="flex-grow" />
        <ContentPreviewMultiCardFooter>
          <ContentPreviewBoostBadge isBoostEligible={isBoostEligible} />
          <ContentPreviewTags tags={tags} />
          <ContentPreviewStats date={date} />
        </ContentPreviewMultiCardFooter>
      </ContentPreviewMultiCardContent>
    </ContentPreviewMultiCard>
  );
};

ContentPreviewMultiCardCompound.displayName = 'ContentPreviewMultiCardCompound';

// === EXPORTS ===

export {
  ContentPreviewMultiCard,
  ContentPreviewMultiCardMedia,
  ContentPreviewMultiCardBackground,
  ContentPreviewMultiCardImageContainer,
  ContentPreviewMultiCardImageContent,
  ContentPreviewMultiCardImage,
  ContentPreviewMultiCardPlaceholder,
  ContentPreviewMultiCardGradient,
  ContentPreviewMultiCardContent,
  ContentPreviewMultiCardFooter,
  ContentPreviewImageGrid,
  ContentPreviewImageGridCell,
  ContentPreviewImageGridPlaceholder,
  ContentPreviewImageGridLoadingCell,
  ContentPreviewImageGridImageCell,
  ContentPreviewMultiCardActions,
  ContentPreviewBoostBadge,
  ContentPreviewTags,
  ContentPreviewStats,
  ContentPreviewMultiCardSkeleton,
  ContentPreviewMultiCardCompound,
};
